import { applySnapshot, flow, types, getSnapshot } from "mobx-state-tree";

import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";

import { TagDictionaryItem } from "./dictionary/TagDictionary";

import { getConvertedDuration } from "utils/calculation";
import { getNextPublishStatus } from "utils/publish";

import {
  ActiveProgram,
  WorkoutCategoryModel,
  PUBLISH_STATUSES,
  WORKOUT_STRUCTURE_TYPES,
} from "constants/models";
import { FILTER_COLUMNS } from "constants/filterConstants";
import { isEmptyObject } from "utils";
import { MSURL } from "../utils/ms";
const corePath = "/core/api/v1";
export const EquipmentModel = types.model({
  id: types.number,
  title: types.string,
  imageUrl: types.string,
});

export const TargetZoneModel = types.model({
  id: types.number,
  title: types.string,
});

export const MovementModel = types
  .model({
    id: types.number,
    workoutMovementInCategoryId: types.maybe(types.number),
    title: types.string,
    duration: types.maybe(types.number),
    imageUrl: types.string,
  })
  .views(self => ({
    get durationText() {
      return getConvertedDuration(self.duration);
    },
  }));

export const WorkoutModel = types
  .model({
    id: types.number,
    title: types.string,
    duration: types.maybe(types.number),
    imageUrl: types.maybe(types.string),
    order: types.maybe(types.number),
    label: types.maybe(types.string),
    day: types.maybe(types.number),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
  })
  .views(self => ({
    get durationText() {
      return getConvertedDuration(self.duration);
    },
  }));

export const WorkoutDetailsModel = types
  .model({
    id: types.number,
    title: types.string,
    awardStar: types.maybe(types.boolean),
    videoUrl: types.maybeNull(types.string),
    videoFileUrl: types.maybeNull(types.string),
    duration: types.maybeNull(types.number),
    instruction: types.maybe(types.string),
    imageUrl: types.maybe(types.string),
    activePrograms: types.array(ActiveProgram),
    categories: types.array(WorkoutCategoryModel),
    equipments: types.array(EquipmentModel),
    movements: types.array(MovementModel),
    targetZone: types.array(TargetZoneModel),
    tags: types.array(TagDictionaryItem),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
    isVideoReady: types.optional(types.boolean, false),
  })
  .views(self => ({
    get durationText() {
      return getConvertedDuration(self.duration);
    },
  }))
  .views(self => ({
    get formData() {
      const uniqueCategoriesMap = new Map();
      self.categories.map(item => uniqueCategoriesMap.set(item.id, item));
      const categoryArray = Array.from(uniqueCategoriesMap.values());

      return {
        ...self,
        file: self.imageUrl,
        durationText: self.durationText,
        category: [
          ...categoryArray.filter(
            item => item.structureType === WORKOUT_STRUCTURE_TYPES.WEEK,
          ),
          ...categoryArray.filter(
            item => item.structureType === WORKOUT_STRUCTURE_TYPES.STANDARD,
          ),
        ].map(({ id, title, structureType }) => ({
          id,
          name: title,
          structureType,
        })),
        targetZone: self?.targetZone?.map(({ id, title }) => ({
          id,
          name: title,
        })),
      };
    },
  }));

const ContentStudioWorkoutsStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/workout/list`, params);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: WorkoutModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(WorkoutDetailsModel),
    isMoreLoading: types.optional(types.boolean, false),
    isWorkoutLoading: types.optional(types.boolean, false),
    isWorkoutManaging: types.optional(types.boolean, false),
    isWorkoutPublishing: types.optional(types.boolean, false),
    isWorkoutCopying: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },

    get workoutsOptions() {
      const { items } = getSnapshot(self);
      return items.map(({ id, title, duration, imageUrl, publishStatus }) => ({
        label: title,
        value: {
          id,
          imageUrl,
          title,
          duration: getConvertedDuration(duration),
          publishStatus,
        },
      }));
    },
  }))
  .actions(self => ({
    updateWorkout(id: number, data: any) {
      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...self.items[index], ...data };
      }
    },
  }))
  .actions(self => ({
    fetchMoreWorkouts: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          const { orderBy, orderByDesc } = self.orderContext || {};

          self.applyPage(self.page + 1);

          const { items } = yield api.get(`/workout/list`, {
            size: DEFAULT_PAGE_SIZE,
            page: self.page,
            search: self.search,
            orderBy,
            orderByDesc,
            ...self.filters,
          });

          applySnapshot(self, {
            ...self,
            items: self.items.concat(items),
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    fetchWorkoutDetails: flow(function*(id?: number) {
      self.isWorkoutLoading = true;
      try {
        const response = yield api.get(
          `/workouts/api/v1/workouts/${id}`,
          undefined,
          undefined,
          MSURL,
        );

        if (response.success) {
          applySnapshot(self, {
            ...self,
            details: response.data,
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isWorkoutLoading = false;
      }
    }),

    fetchTargetZoneTagging: flow(function*(id?: number) {
      try {
        self.isWorkoutLoading = true;
        const targetZoneDetails = yield api.get(
          `${corePath}/workout-target-zone/${id}`,
          undefined,
          undefined,
          MSURL,
        );
        applySnapshot(self, {
          ...self,
          details: {
            ...self.details,
            ...targetZoneDetails,
          },
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isWorkoutLoading = false;
      }
    }),

    createTaggingTargetZone: flow(function*(payload: any) {
      self.isWorkoutManaging = true;

      try {
        const { id } = yield api.post(
          `${corePath}/workout-target-zone/${payload.id}`,
          payload,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isWorkoutManaging = false;
      }
    }),

    createWorkout: flow(function*(payload: any) {
      self.isWorkoutManaging = true;

      try {
        const response = yield api.post(
          `/workouts/api/v1/workouts`,
          payload,
          undefined,
          MSURL,
        );

        return response.data.id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isWorkoutManaging = false;
      }
    }),

    editWorkoutInfo: flow(function*(payload: any) {
      self.isWorkoutManaging = true;

      try {
        return yield api.put(
          `/workouts/api/v1/workouts/${payload.id}`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isWorkoutManaging = false;
      }
    }),

    editWorkoutImage: flow(function*(payload: any) {
      self.isWorkoutManaging = true;

      try {
        return yield api.putFormData(
          `/workout/${payload.id}/photo/upload`,
          payload,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isWorkoutManaging = false;
      }
    }),

    changeWorkoutPublishStatus: flow(function*(id: number, status: string) {
      self.isWorkoutPublishing = true;

      try {
        const newStatus = getNextPublishStatus(status);
        if (newStatus) {
          const { success } = yield api.post(`/workout/${id}/status`, {
            status: newStatus,
          });
          if (success) {
            self.updateWorkout(id, { publishStatus: newStatus });
          }
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isWorkoutPublishing = false;
      }
    }),

    archiveWorkout: flow(function*(id: number) {
      self.isWorkoutManaging = true;

      try {
        return yield api.post(`/workout/${id}/archive`, {});
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isWorkoutManaging = false;
      }
    }),

    copyWorkout: flow(function*(id: number) {
      self.isWorkoutCopying = true;

      try {
        yield api.post(`/workout/${id}/copy`, {});
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isWorkoutCopying = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },
  }));

export const ContentStudioArchivedWorkoutsStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/workout/archived/list`, params);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: WorkoutModel,
  searchBy: "search",
})
  .props({
    isManaging: types.optional(types.boolean, false),
    isMoreLoading: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },

    get options() {
      const { items } = getSnapshot(self);
      return items.map(({ id, title, duration, imageUrl, publishStatus }) => ({
        label: title,
        value: {
          id,
          imageUrl,
          title,
          duration: getConvertedDuration(duration),
          publishStatus,
        },
      }));
    },
  }))
  .actions(self => ({
    fetchMore: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);

          const { items } = yield api.get(`/workout/archived/list`, {
            size: DEFAULT_PAGE_SIZE,
            page: self.page,
          });

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    restore: flow(function*(payload: any) {
      self.isManaging = true;

      try {
        return yield api.post(`/workout/restore`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isManaging = false;
      }
    }),
  }))
  .create();

export default ContentStudioWorkoutsStore.create();
